/**
 * Season Grade Helper (JS mirror of personal-training-BE/season-grade-helper.php).
 *
 * Pure functions for mapping between birth years, U-grades, and season-year
 * strings. No HTTP, no Vuex — safe to import from any component, service, or
 * test. Must stay function-for-function identical to the PHP version.
 *
 * Convention:
 *   - Season year strings use the format "2025-26" (matching
 *     SeasonPhases::getSeasonYear() on the backend). The reference year for
 *     grade math is the SECOND year in the pair.
 *   - U-grade = referenceYear - YEAR(dob). SFA / UEFA calendar-year cohorts.
 *   - Dual-band U-grades (one squad covering two consecutive birth years)
 *     are 17 and 19 per SFA convention.
 */

// SFA dual-band U-grades. Mirror of SeasonGrade::DUAL_BAND_U_GRADES.
export const DUAL_BAND_U_GRADES = Object.freeze([17, 19]);

/**
 * "2025-26" → 2026
 *
 * Throws on malformed input. Handles century rollover ("2099-00" → 2100).
 */
export function referenceYear(seasonYear) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(seasonYear || ""));
  if (!m) {
    throw new Error(
      `Invalid season year format: '${seasonYear}' (expected e.g. '2025-26')`,
    );
  }
  const startYear = parseInt(m[1], 10);
  let endYear = parseInt(m[1].slice(0, 2) + m[2], 10);
  if (endYear < startYear) {
    endYear += 100;
  }
  return endYear;
}

/** (10, "2025-26") → 2016 */
export function birthYearForUGrade(uGrade, seasonYear) {
  return referenceYear(seasonYear) - uGrade;
}

/** (2016, "2025-26") → 10 */
export function uGradeForBirthYear(birthYear, seasonYear) {
  return referenceYear(seasonYear) - birthYear;
}

/**
 * (16, 17, "2025-26") → [2009, 2010]
 *
 * Ascending order. Used by reminders/notifications targeting whole squads.
 */
export function birthYearsForSquad(uMin, uMax, seasonYear) {
  if (uMin > uMax) {
    throw new Error(`uMin (${uMin}) must be <= uMax (${uMax})`);
  }
  const ref = referenceYear(seasonYear);
  const start = ref - uMax;
  const end = ref - uMin;
  const result = [];
  for (let y = start; y <= end; y += 1) result.push(y);
  return result;
}

/**
 * ("2016-04-12", "2025-26") → 10
 *
 * Returns null if dob is empty / unparseable. Year-only parsing matches the
 * SFA calendar-year cohort convention; birth month/day are irrelevant.
 */
export function uGradeForDob(dob, seasonYear) {
  if (!dob) return null;
  const m = /^(\d{4})/.exec(String(dob));
  if (!m) return null;
  return uGradeForBirthYear(parseInt(m[1], 10), seasonYear);
}

/** Whether a U-grade is one of the dual-band grades. */
export function isDualBand(uGrade) {
  return DUAL_BAND_U_GRADES.includes(uGrade);
}

/**
 * Returns the current season-year string ("2025-26"). June onward rolls
 * to the new season. Mirrors SeasonGrade::currentSeasonYear() in PHP.
 *
 * @param {Date} [today] Optional override for tests.
 * @returns {string}
 */
export function currentSeasonYear(today = new Date()) {
  const month = today.getMonth() + 1;
  const year = today.getFullYear();
  return month >= 6
    ? `${year}-${String((year + 1) % 100).padStart(2, "0")}`
    : `${year - 1}-${String(year % 100).padStart(2, "0")}`;
}

/**
 * Given a list of birth-year strings, expand it to include the dual-band
 * partner of any year that's part of a dual-band cohort.
 *
 * Example: in season 2025-26 (referenceYear=2026), U17 dual-band covers
 * birth years 2009-2010. So:
 *   - ["2009"] → ["2009", "2010"]
 *   - ["2010"] → ["2010", "2009"]
 *   - ["2014"] → ["2014"]            (U12, not dual)
 *
 * Replaces the hardcoded `if (ageGroup.includes("2009")) push("2010")`
 * chains that would silently target the wrong years next season.
 *
 * @param {string[]} ageGroup
 * @param {string} [seasonYear]  Defaults to current season.
 * @returns {string[]}
 */
export function expandDualBandPairs(ageGroup, seasonYear = currentSeasonYear()) {
  if (!Array.isArray(ageGroup) || ageGroup.length === 0) return [];
  const expanded = new Set(ageGroup.map(String));
  for (const yearStr of [...expanded]) {
    const year = parseInt(yearStr, 10);
    if (Number.isNaN(year)) continue;
    const uGrade = uGradeForBirthYear(year, seasonYear);
    if (DUAL_BAND_U_GRADES.includes(uGrade)) {
      // This year is the OLDER half of a dual-band — partner is younger.
      expanded.add(String(birthYearForUGrade(uGrade - 1, seasonYear)));
    } else if (DUAL_BAND_U_GRADES.includes(uGrade + 1)) {
      // This year is the YOUNGER half — partner is older.
      expanded.add(String(birthYearForUGrade(uGrade + 1, seasonYear)));
    }
  }
  return [...expanded];
}

/**
 * Given a player's target U-grade and a list of squads in their club,
 * return the squad whose [u_grade_min..u_grade_max] range contains that
 * grade — or null if no squad fits (typically the U19 graduate case).
 *
 * Squads with null u_grade_min/max are ignored (un-categorised squads
 * like First Team / archived).
 *
 * If multiple squads overlap, the tightest-range squad wins; lower id
 * breaks ties deterministically.
 *
 * @param {number} newUGrade
 * @param {Array<{id: number, u_grade_min: ?number, u_grade_max: ?number}>} clubSquads
 * @returns {object|null}
 */
export function suggestTargetSquad(newUGrade, clubSquads) {
  const matches = (clubSquads || []).filter((s) => {
    const min = s?.u_grade_min;
    const max = s?.u_grade_max;
    if (min === null || min === undefined) return false;
    if (max === null || max === undefined) return false;
    return newUGrade >= Number(min) && newUGrade <= Number(max);
  });
  if (matches.length === 0) return null;

  matches.sort((a, b) => {
    const rangeA = Number(a.u_grade_max) - Number(a.u_grade_min);
    const rangeB = Number(b.u_grade_max) - Number(b.u_grade_min);
    if (rangeA !== rangeB) return rangeA - rangeB;
    return Number(a.id || 0) - Number(b.id || 0);
  });
  return matches[0];
}
